import React, { useState, useEffect, useCallback, useRef } from 'react';
import db from '../services/database';
import { useAnnotations } from '../contexts/AnnotationContext';
import Icon from '../components/UI/Icon';
import './ThemesPage.css';

// Note: TensorFlow.js semantic search can be enabled by installing:
// npm install @tensorflow/tfjs @tensorflow-models/universal-sentence-encoder
// For now, we use simple text-based search which works offline and is lightweight.

function ThemesPage() {
  const { highlights, notes } = useAnnotations();
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTheme, setNewTheme] = useState({ name: '', description: '', color: '#8B7355' });
  const [mindMapData, setMindMapData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const mindMapRef = useRef(null);
  const jmRef = useRef(null);

  // Load themes
  useEffect(() => {
    async function loadThemes() {
      try {
        const savedThemes = await db.themes.toArray();
        setThemes(savedThemes);
      } catch (err) {
        console.error('Failed to load themes:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadThemes();
  }, []);

  // Initialize mind map
  useEffect(() => {
    if (selectedTheme && mindMapData && mindMapRef.current) {
      const initMindMap = async () => {
        try {
          // Dynamically import jsMind
          const jsMindModule = await import('jsmind');
          const jsMind = jsMindModule.default || jsMindModule;
          
          if (jmRef.current) {
            jmRef.current.destroy?.();
          }

          const options = {
            container: mindMapRef.current,
            theme: 'primary',
            editable: true,
            mode: 'full'
          };

          jmRef.current = new jsMind(options);
          jmRef.current.show(mindMapData);
        } catch (err) {
          console.warn('jsMind not available:', err);
        }
      };
      
      initMindMap();
    }
  }, [selectedTheme, mindMapData]);

  // Create theme
  const handleCreateTheme = useCallback(async () => {
    if (!newTheme.name.trim()) return;

    const theme = {
      ...newTheme,
      verses: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      const id = await db.themes.add(theme);
      const savedTheme = { ...theme, id };
      setThemes(prev => [...prev, savedTheme]);
      setNewTheme({ name: '', description: '', color: '#8B7355' });
      setIsCreating(false);
      setSelectedTheme(savedTheme);
    } catch (err) {
      console.error('Failed to create theme:', err);
    }
  }, [newTheme]);

  // Add verse to theme
  const handleAddVerse = useCallback(async (verse) => {
    if (!selectedTheme) return;

    const updatedVerses = [...(selectedTheme.verses || []), verse];
    const updates = {
      verses: updatedVerses,
      updatedAt: new Date().toISOString()
    };

    try {
      await db.themes.update(selectedTheme.id, updates);
      setSelectedTheme(prev => ({ ...prev, ...updates }));
      setThemes(prev => prev.map(t => 
        t.id === selectedTheme.id ? { ...t, ...updates } : t
      ));
      updateMindMapData({ ...selectedTheme, ...updates });
    } catch (err) {
      console.error('Failed to add verse:', err);
    }
  }, [selectedTheme]);

  // Remove verse from theme
  const handleRemoveVerse = useCallback(async (verseIndex) => {
    if (!selectedTheme) return;

    const updatedVerses = selectedTheme.verses.filter((_, i) => i !== verseIndex);
    const updates = {
      verses: updatedVerses,
      updatedAt: new Date().toISOString()
    };

    try {
      await db.themes.update(selectedTheme.id, updates);
      setSelectedTheme(prev => ({ ...prev, ...updates }));
      setThemes(prev => prev.map(t => 
        t.id === selectedTheme.id ? { ...t, ...updates } : t
      ));
      updateMindMapData({ ...selectedTheme, ...updates });
    } catch (err) {
      console.error('Failed to remove verse:', err);
    }
  }, [selectedTheme]);

  // Delete theme
  const handleDeleteTheme = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this theme?')) return;

    try {
      await db.themes.delete(id);
      setThemes(prev => prev.filter(t => t.id !== id));
      if (selectedTheme?.id === id) {
        setSelectedTheme(null);
        setMindMapData(null);
      }
    } catch (err) {
      console.error('Failed to delete theme:', err);
    }
  }, [selectedTheme]);

  // Update mind map data structure
  const updateMindMapData = (theme) => {
    const data = {
      meta: { name: theme.name, author: 'TruthBound', version: '1.0' },
      format: 'node_array',
      data: [
        { id: 'root', topic: theme.name, isroot: true },
        ...(theme.verses || []).map((verse, index) => ({
          id: `verse_${index}`,
          parentid: 'root',
          topic: `${verse.reference}\n${verse.text?.substring(0, 50)}...`
        }))
      ]
    };
    setMindMapData(data);
  };

  // Select theme
  const handleSelectTheme = (theme) => {
    setSelectedTheme(theme);
    updateMindMapData(theme);
  };

  // Semantic search with TensorFlow.js
  const handleSemanticSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);

    // Get all verses from highlights and notes
    const allVerses = [
      ...highlights.map(h => ({ type: 'highlight', reference: h.verseReference, text: h.selectedText })),
      ...notes.map(n => ({ type: 'note', reference: n.verseReference, text: n.content }))
    ];

    if (allVerses.length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Text-based search (lightweight alternative to TensorFlow semantic search)
    const query = searchQuery.toLowerCase();
    const queryWords = query.split(/\s+/).filter(w => w.length > 2);
    
    const results = allVerses
      .map(v => {
        const text = (v.text || '').toLowerCase();
        const ref = (v.reference || '').toLowerCase();
        
        // Score based on word matches
        let score = 0;
        if (text.includes(query) || ref.includes(query)) {
          score += 10; // Exact phrase match
        }
        queryWords.forEach(word => {
          if (text.includes(word)) score += 2;
          if (ref.includes(word)) score += 1;
        });
        
        return { ...v, score };
      })
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
    
    setSearchResults(results);
    setIsSearching(false);
  }, [searchQuery, highlights, notes]);

  // Color options
  const COLORS = [
    '#8B7355', '#6B8E4E', '#5D7A99', '#9B6B6B', '#8B6B99',
    '#C4A35A', '#7A9B7A', '#7A7A9B', '#9B8B7A', '#6B9B9B'
  ];

  return (
    <div className="themes-page page">
      <header className="page-header">
        <h1 className="page-title">Thematic Collections</h1>
        <button className="btn btn-primary" onClick={() => setIsCreating(true)}>
          <Icon name="plus" size={18} />
          New Theme
        </button>
      </header>

      <div className="themes-content">
        {/* Sidebar */}
        <aside className="themes-sidebar">
          {/* Semantic Search */}
          <div className="search-section">
            <h3>Semantic Search</h3>
            <p className="search-description">
              Find related verses across your highlights and notes
            </p>
            <div className="search-form">
              <input
                type="text"
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSemanticSearch()}
                placeholder="Search by concept..."
              />
              <button 
                className="btn btn-primary"
                onClick={handleSemanticSearch}
                disabled={isSearching}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result, index) => (
                  <div key={index} className="search-result">
                    <div className="result-header">
                      <span className="result-reference">{result.reference}</span>
                      <span className={`result-type ${result.type}`}>{result.type}</span>
                    </div>
                    <p className="result-text">{result.text?.substring(0, 100)}...</p>
                    {selectedTheme && (
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleAddVerse({
                          reference: result.reference,
                          text: result.text
                        })}
                      >
                        <Icon name="plus" size={14} />
                        Add to Theme
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Theme List */}
          <div className="theme-list">
            <h3>Your Themes</h3>
            {isLoading ? (
              <p className="loading-text">Loading themes...</p>
            ) : themes.length === 0 ? (
              <p className="empty-text">No themes yet. Create one to start organizing verses.</p>
            ) : (
              themes.map(theme => (
                <button
                  key={theme.id}
                  className={`theme-item ${selectedTheme?.id === theme.id ? 'active' : ''}`}
                  onClick={() => handleSelectTheme(theme)}
                  style={{ '--theme-color': theme.color }}
                >
                  <div className="theme-color-dot" style={{ background: theme.color }} />
                  <div className="theme-info">
                    <span className="theme-name">{theme.name}</span>
                    <span className="theme-count">{theme.verses?.length || 0} verses</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="themes-main">
          {isCreating ? (
            <div className="create-theme-panel">
              <h2>Create New Theme</h2>
              <div className="form-group">
                <label className="form-label">Theme Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTheme.name}
                  onChange={(e) => setNewTheme(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., God's Promises, Love, Faith..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={newTheme.description}
                  onChange={(e) => setNewTheme(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this theme..."
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div className="color-options">
                  {COLORS.map(color => (
                    <button
                      key={color}
                      className={`color-option ${newTheme.color === color ? 'selected' : ''}`}
                      style={{ background: color }}
                      onClick={() => setNewTheme(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={() => setIsCreating(false)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateTheme}
                  disabled={!newTheme.name.trim()}
                >
                  Create Theme
                </button>
              </div>
            </div>
          ) : selectedTheme ? (
            <div className="theme-detail">
              <div className="theme-header" style={{ '--theme-color': selectedTheme.color }}>
                <div className="theme-color-bar" style={{ background: selectedTheme.color }} />
                <div className="theme-header-content">
                  <h2>{selectedTheme.name}</h2>
                  {selectedTheme.description && (
                    <p className="theme-description">{selectedTheme.description}</p>
                  )}
                  <span className="verse-count">{selectedTheme.verses?.length || 0} verses</span>
                </div>
                <button 
                  className="btn btn-ghost btn-icon"
                  onClick={() => handleDeleteTheme(selectedTheme.id)}
                >
                  <Icon name="trash" size={18} />
                </button>
              </div>

              {/* Mind Map Container */}
              <div className="mind-map-container" ref={mindMapRef}>
                {(!selectedTheme.verses || selectedTheme.verses.length === 0) && (
                  <div className="mind-map-placeholder">
                    <Icon name="themes" size={48} />
                    <p>Add verses to see them visualized</p>
                  </div>
                )}
              </div>

              {/* Verse List */}
              <div className="verses-section">
                <h3>Collected Verses</h3>
                {selectedTheme.verses?.length > 0 ? (
                  <div className="verses-list">
                    {selectedTheme.verses.map((verse, index) => (
                      <div key={index} className="verse-card">
                        <div className="verse-reference">{verse.reference}</div>
                        <p className="verse-text">{verse.text}</p>
                        <button 
                          className="btn btn-ghost btn-sm remove-btn"
                          onClick={() => handleRemoveVerse(index)}
                        >
                          <Icon name="x" size={14} />
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-verses">
                    No verses added yet. Use the semantic search to find related verses 
                    or add them while reading Scripture.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <Icon name="themes" size={64} />
              <h3>Select or Create a Theme</h3>
              <p>Organize related verses into thematic collections for deeper study</p>
              <button className="btn btn-primary" onClick={() => setIsCreating(true)}>
                <Icon name="plus" size={18} />
                Create Your First Theme
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ThemesPage;
